import TemplateDto from '@/services/skills/Dto/TemplateDto';
import PageTemplateDto from '@/services/skills/Dto/PageTemplateDto';
import TemplateRequestDto from '@/services/skills/Dto/TemplateRequestDto';
import TemplatesApiRepository from '@/services/skills/TemplatesApiRepository';
import AgentSettingsService from '@/services/account/agentSettingsService';
import { v4 as uuidv4 } from 'uuid';
import { SHIPPING_EVENTS } from '@/components/Skills/LixsaSkills/ShippingUpdateNotifications/Utils/ShippingEventsConfig';
import { getDefaultShippingTemplate } from '@/components/Skills/LixsaSkills/ShippingUpdateNotifications/Utils/ShippingUpdateDefaultTemplates';

const FLOW_TYPE = 'SHIPPING_UPDATE'; // Existe en el enum del backend: FlowType.java:10
const SHIPPING_UPDATE_SKILL_ID = 'd0b3e289-e8f3-4b79-b0a9-4c857c880ac6';
const CATEGORY = 'UTILITY'; // Categoría de META. Nunca Category.SHIPPING_UPDATE (deprecada en 2023)
const DEFAULT_EXCLUDE_TAGS = 'lx-no-shipping';

const state = () => ({
    templatesList: null,
    currentLanguage: 'en',
    currentTemplate: null,
    availableLanguages: [],
    /** config_values de la skill, ya deserializados. */
    skillConfig: {
        shipping_events: {}, // { evento: { idioma: templateUuid } } — objeto, no string
        exclude_tags: DEFAULT_EXCLUDE_TAGS,
    },
});

const formatLanguage = (language) => {
    if (!language) return '';
    const [base, region] = language.split('_');
    return region ? `${base.toLowerCase()}_${region.toUpperCase()}` : base.toLowerCase();
};

/**
 * Acepta las dos formas de `shipping_events` y devuelve siempre la anidada.
 *  - { "in_transit": "uuid" }            → { in_transit: { '*': 'uuid' } }
 *  - { "in_transit": { "es": "uuid" } }  → tal cual
 * Descarta claves que no sean uno de los 9 eventos conocidos.
 *
 * @param {string|Object|null} raw Valor crudo de config_values.shipping_events.
 * @returns {Object<string, Object<string, string>>}
 */
const parseShippingEvents = (raw) => {
    if (!raw) return {};

    let parsed = raw;
    if (typeof raw === 'string') {
        try {
            parsed = JSON.parse(raw);
        } catch (error) {
            console.error('shipping_events malformado, se ignora:', raw, error?.message);
            return {};
        }
    }
    if (!parsed || typeof parsed !== 'object') return {};

    const valid = new Set(SHIPPING_EVENTS.map((event) => event.value));

    return Object.entries(parsed).reduce((acc, [event, value]) => {
        if (!valid.has(event) || !value) return acc;
        acc[event] = typeof value === 'string' ? { '*': value } : { ...value };
        return acc;
    }, {});
};

/**
 * Normaliza el dominio público de la tienda, sin protocolo ni barra final.
 * @param {Object} rootState
 * @returns {string}
 */
const buildShopDomain = (rootState) =>
    (rootState.user.account?.shop_public_domain || 'lixsa.ai').replace(/^https?:\/\//, '').replace(/\/$/, '');

/**
 * Aplica el workaround de URL dinámica de Meta a los botones.
 *
 * Meta exige que una URL dinámica empiece por https:// y que la variable esté solo
 * al FINAL, así que el dominio queda fijo al de la tienda. Por eso el botón de
 * seguimiento apunta a `order_status_url` (dominio de la tienda) y nunca a la URL
 * del transportista: si hace falta esa, va en el cuerpo como {{tracking_url}}.
 *
 * @param {Array} buttons
 * @param {string} shopDomain
 * @returns {Array}
 */
const formatButtons = (buttons, shopDomain) =>
    (buttons || []).map((btn) => {
        if (btn.type === 'URL' && btn.url_type === 'DYNAMIC') {
            return {
                ...btn,
                url: `https://${shopDomain}/{{1}}`,
                example: [`https://${shopDomain}/redirect`],
            };
        }
        return btn;
    });

const getters = {
    getCurrentTemplate: (state) => state.currentTemplate,
    getCurrentLanguage: (state) => state.currentLanguage,
    getAvailableLanguages: (state) => state.availableLanguages,
    currentTemplateIsSavedInTemplatesList: (state) => {
        if (!state.currentTemplate) return false;
        return (state.templatesList?.content || []).some((template) => template.uuid === state.currentTemplate.uuid);
    },
    getCurrentTemplateStatus: (state) => {
        if (!state.currentTemplate) return null;
        return state.currentTemplate.status || 'NEW';
    },

    getShippingEvents: (state) => state.skillConfig.shipping_events,

    getExcludeTags: (state) =>
        (state.skillConfig.exclude_tags || '')
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean),

    /**
     * Eventos asignados a una plantilla concreta, en cualquier idioma.
     * @returns {(templateUuid: string) => string[]}
     */
    getEventsByTemplate: (state) => (templateUuid) =>
        Object.entries(state.skillConfig.shipping_events)
            .filter(([, byLang]) => Object.values(byLang).includes(templateUuid))
            .map(([event]) => event),

    /**
     * Quién cubre hoy un (evento, idioma). null si está libre.
     * @returns {(event: string, lang: string) => string|null}
     */
    getOwnerOfEvent: (state) => (event, lang) => {
        const byLang = state.skillConfig.shipping_events[event];
        if (!byLang) return null;
        return byLang[formatLanguage(lang)] || byLang['*'] || null;
    },

    /** Eventos que ningún idioma cubre: no se envía nada para ellos. */
    getUnassignedEvents: (state) =>
        SHIPPING_EVENTS.map((event) => event.value).filter((event) => {
            const byLang = state.skillConfig.shipping_events[event];
            return !byLang || Object.keys(byLang).length === 0;
        }),
};

const mutations = {
    SET_TEMPLATE_LIST(state, templatesList) {
        state.templatesList = templatesList;
    },
    SET_CURRENT_LANGUAGE(state, language) {
        state.currentLanguage = language;
    },
    SET_AVAILABLE_LANGUAGES(state, languages) {
        state.availableLanguages = languages;
    },
    SET_CURRENT_TEMPLATE(state, template) {
        state.currentTemplate = template;
    },
    UPDATE_TEMPLATE_IN_LIST(state, { uuid, templateContent, templateName, buttons }) {
        if (state.currentTemplate && state.currentTemplate.uuid === uuid) {
            state.currentTemplate = {
                ...state.currentTemplate,
                body: templateContent,
                ...(templateName ? { templateName } : {}),
                ...(buttons !== undefined ? { buttons } : {}),
            };
        }
        if (state.templatesList?.content) {
            state.templatesList = {
                ...state.templatesList,
                content: state.templatesList.content.map((template) =>
                    template.uuid === uuid
                        ? {
                              ...template,
                              body: templateContent,
                              ...(templateName ? { templateName } : {}),
                              ...(buttons !== undefined ? { buttons } : {}),
                          }
                        : template,
                ),
            };
        }
    },

    SET_SKILL_CONFIG(state, config) {
        state.skillConfig = { ...state.skillConfig, ...config };
    },

    /**
     * Reescribe de golpe los eventos de UNA plantilla en UN idioma: la suelta de todos
     * los eventos de ese idioma y la asigna solo a los pedidos.
     *
     * Pisa al ocupante anterior a propósito: el bloqueo del conflicto es cosa de la UI,
     * que ya ha pedido confirmación. Aquí solo se ejecuta la reasignación confirmada.
     */
    SET_TEMPLATE_EVENTS(state, { templateUuid, language, events }) {
        const lang = formatLanguage(language);
        const next = {};

        Object.entries(state.skillConfig.shipping_events).forEach(([event, byLang]) => {
            const clean = { ...byLang };
            if (clean[lang] === templateUuid) delete clean[lang];
            if (clean['*'] === templateUuid) delete clean['*'];
            if (Object.keys(clean).length) next[event] = clean;
        });

        events.forEach((event) => {
            next[event] = { ...(next[event] || {}), [lang]: templateUuid };
        });

        state.skillConfig = { ...state.skillConfig, shipping_events: next };
    },

    /** Borra toda huella de una plantilla eliminada. */
    PURGE_TEMPLATE_EVENTS(state, templateUuid) {
        const next = {};

        Object.entries(state.skillConfig.shipping_events).forEach(([event, byLang]) => {
            const clean = Object.fromEntries(Object.entries(byLang).filter(([, uuid]) => uuid !== templateUuid));
            if (Object.keys(clean).length) next[event] = clean;
        });

        state.skillConfig = { ...state.skillConfig, shipping_events: next };
    },

    SET_EXCLUDE_TAGS(state, csv) {
        state.skillConfig = { ...state.skillConfig, exclude_tags: csv };
    },
};

const actions = {
    async fetchTemplatesList({ commit }) {
        try {
            const templates = await TemplatesApiRepository.getTemplates(FLOW_TYPE);
            commit('SET_TEMPLATE_LIST', templates);
            return templates;
        } catch (error) {
            console.error('Error fetching shipping templates:', error);
            throw error;
        }
    },

    /**
     * Prepara el editor para una plantilla NUEVA.
     *
     * Sustituye al `fetchCurrentTemplate` de ThanksOrder: allí se busca "la plantilla
     * del idioma actual", pero aquí hay N plantillas por idioma y esa búsqueda elegiría
     * una al azar. La selección es siempre explícita.
     */
    async prepareNewTemplate({ commit, dispatch }) {
        commit('SET_CURRENT_TEMPLATE', null);
        await dispatch('fetchDefaultTemplate');
    },

    /**
     * Carga el cuerpo por defecto. Blindado: el backend no tiene TemplateVariables
     * registradas para SHIPPING_UPDATE (devuelve 200 con cuerpo vacío), así que sin
     * el catálogo local el editor abriría en blanco.
     */
    async fetchDefaultTemplate({ commit, state }) {
        let contentExample = null;

        try {
            const templateVariables = await TemplatesApiRepository.getTemplateVariables(
                SHIPPING_UPDATE_SKILL_ID,
                FLOW_TYPE,
                state.currentLanguage,
            );
            contentExample = templateVariables?.contentExample || null;
        } catch (error) {
            console.warn('Sin template-variables para SHIPPING_UPDATE, uso el catálogo local:', error?.message);
        }

        commit(
            'SET_CURRENT_TEMPLATE',
            new TemplateDto.Builder()
                .withUuid(null)
                .withTemplateType('FLOW')
                .withFlowType(FLOW_TYPE)
                .withSkillId(SHIPPING_UPDATE_SKILL_ID)
                .withLanguage(state.currentLanguage)
                .withBody(contentExample || getDefaultShippingTemplate(state.currentLanguage))
                .withVariables([])
                .build(),
        );
    },

    async fetchAvailableLanguages({ commit }) {
        try {
            const languages = await TemplatesApiRepository.getAvailableLanguages();
            commit('SET_AVAILABLE_LANGUAGES', languages);
        } catch (error) {
            console.error('Error fetching available languages:', error);
            throw error;
        }
    },

    async fetchCurrentLanguage({ rootGetters, state, commit }) {
        const hasShopifySkill = rootGetters['agentSettings/hasShopifySkill'];
        if (!hasShopifySkill) {
            commit('SET_CURRENT_LANGUAGE', 'en');
            return;
        }

        const shopifySkill = rootGetters['agentSettings/getSkillByInternalName']('Shopify');
        const shopifyLanguage = shopifySkill?.config_values?.countryCode;
        const language = state.availableLanguages.find((item) => item.tag === shopifyLanguage);

        commit('SET_CURRENT_LANGUAGE', language?.tag || 'en');
    },

    /**
     * Crea o actualiza la plantilla y DEVUELVE su uuid, que hace falta inmediatamente
     * para escribir la asignación de eventos en config_values.
     *
     * @returns {Promise<string>} uuid de la plantilla guardada.
     */
    async updateTemplate(
        { commit, state, getters, dispatch, rootState },
        { templateContent, templateName, accountId, buttons, businessAccountId },
    ) {
        const shopDomain = buildShopDomain(rootState);
        const formattedButtons = formatButtons(buttons, shopDomain);

        try {
            if (getters.currentTemplateIsSavedInTemplatesList && state.currentTemplate) {
                const templateRequest = new TemplateRequestDto(state.currentTemplate);
                templateRequest.body = templateContent;
                if (templateName) templateRequest.template_name = templateName;
                templateRequest.buttons = formattedButtons;

                await TemplatesApiRepository.updateTemplate(state.currentTemplate.uuid, templateRequest);
                commit('UPDATE_TEMPLATE_IN_LIST', {
                    uuid: state.currentTemplate.uuid,
                    templateContent,
                    templateName,
                    buttons: formattedButtons,
                });

                return state.currentTemplate.uuid;
            }

            const newUuid = uuidv4();
            const templateRequest = new TemplateRequestDto.Builder()
                .withUuid(newUuid)
                .withAccountId(accountId)
                .withBody(templateContent)
                .withLanguage(formatLanguage(state.currentLanguage))
                .withTemplateName(templateName || 'shipping_update')
                .withCategory(CATEGORY)
                .withType('TEXT')
                .withTemplateType('FLOW')
                .withFlowType(FLOW_TYPE)
                .withSkillId(SHIPPING_UPDATE_SKILL_ID)
                .withChannelType('WHATSAPP')
                .withButtons(formattedButtons)
                .withBusinessAccountId(businessAccountId)
                .build();

            await TemplatesApiRepository.createTemplate(templateRequest);
            await dispatch('fetchTemplatesList');

            return newUuid;
        } catch (error) {
            console.error('Error updating shipping template:', error);
            const errorMsg = error.response?.data?.message || error.response?.data || error.message || 'Unknown error';
            throw new Error(`[Backend Error]: ${JSON.stringify(errorMsg)}`);
        }
    },

    /**
     * Borra la plantilla y limpia sus asignaciones, para no dejar un evento apuntando
     * a una plantilla inexistente (n8n dejaría de enviar sin decir por qué).
     */
    async deleteTemplate({ commit, state, dispatch }, { templateUuid, isSkillEnabled }) {
        try {
            const foundTemplate = state.templatesList.content.find((template) => template.uuid === templateUuid);
            if (!foundTemplate) return;

            const templateRequest = new TemplateRequestDto(foundTemplate);
            await TemplatesApiRepository.deleteTemplate(templateUuid, templateRequest);

            const updatedContent = state.templatesList.content.filter((template) => template.uuid !== templateUuid);
            commit(
                'SET_TEMPLATE_LIST',
                new PageTemplateDto({
                    ...state.templatesList,
                    content: updatedContent,
                    numberOfElements: updatedContent.length,
                }),
            );

            commit('PURGE_TEMPLATE_EVENTS', templateUuid);
            await dispatch('saveSkillConfig', { isSkillEnabled });
        } catch (error) {
            console.error('Error deleting shipping template:', error);
            throw error;
        }
    },

    setCurrentLanguage({ commit }, language) {
        commit('SET_CURRENT_LANGUAGE', formatLanguage(language));
    },

    selectTemplate({ commit }, template) {
        commit('SET_CURRENT_TEMPLATE', new TemplateDto(template));
    },

    /** Lee config_values de la skill y los deserializa. */
    async fetchSkillConfig({ commit }) {
        try {
            const response = await AgentSettingsService.getAgentSettings();
            const skills = response.data?.skills || [];
            const skill = skills.find((item) => item.skill_id === SHIPPING_UPDATE_SKILL_ID);
            const config = skill?.config_values || skill?.configValues || {};

            commit('SET_SKILL_CONFIG', {
                shipping_events: parseShippingEvents(config.shipping_events),
                exclude_tags: config.exclude_tags ?? DEFAULT_EXCLUDE_TAGS,
            });
        } catch (error) {
            console.error('Error fetching shipping update config:', error);
        }
    },

    /**
     * Persiste SOLO las claves editables. El backend hace merge (putAll), así que las
     * claves que escriba n8n se conservan.
     *
     * `isSkillEnabled` se propaga tal cual: guardar la configuración NO activa la skill.
     * (reviewRequest.js llama a modifyAgentSettings sin ese argumento y su valor por
     * defecto es `true`, con lo que guardar reactiva la skill sin querer.)
     *
     * @param {{isSkillEnabled: boolean}} payload
     */
    async saveSkillConfig({ state, rootState }, { isSkillEnabled }) {
        const config = {
            shipping_events: JSON.stringify(state.skillConfig.shipping_events),
            exclude_tags: state.skillConfig.exclude_tags || '',
        };
        const accountId = rootState.user.user.account_id;
        const skillExists = (rootState.agentSettings.agentSettings?.skills || []).some(
            (skill) => skill.skill_id === SHIPPING_UPDATE_SKILL_ID,
        );

        if (skillExists) {
            await AgentSettingsService.modifyAgentSettings(
                config,
                SHIPPING_UPDATE_SKILL_ID,
                undefined,
                Boolean(isSkillEnabled),
            );
            return;
        }

        // El alta usa `config_schema_values`; la modificación usa `config_values`.
        // El desajuste es del servicio (agentSettingsService.js:51-54 vs :76-89).
        await AgentSettingsService.addSkillToAgentSettings({
            skill_id: SHIPPING_UPDATE_SKILL_ID,
            agent_id: accountId,
            account_id: accountId,
            config_schema_values: config,
            enabled: false,
        });
    },

    async subscribeToTemplateUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/template`;
        try {
            await dispatch(
                'websocket/subscribe',
                { topic, callback: (data) => dispatch('handleTemplateEvent', data) },
                { root: true },
            );
        } catch (error) {
            console.error(`Failed to subscribe to ${topic}:`, error);
        }
    },

    unsubscribeFromTemplateUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/template`;
        dispatch('websocket/unsubscribe', { topic }, { root: true });
    },

    handleTemplateEvent({ commit, state, dispatch }, event) {
        if (!event || !event.data) return;

        const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        const source = eventData;

        const template = new TemplateDto.Builder()
            .withUuid(source.uuid)
            .withAccountId(source.accountId || source.account_id)
            .withBody(source.body)
            .withHeader(source.header)
            .withChannelType(source.channelType || source.channel_type)
            .withTemplateName(source.templateName || source.template_name)
            .withLanguage(source.language)
            .withCategory(source.category)
            .withStatus(source.status)
            .withScore(source.score)
            .withType(source.type)
            .withTemplateType(source.templateType || source.template_type)
            .withFlowType(source.flowType || source.flow_type)
            .withSkillId(source.skillId || source.skill_id)
            .withInternalTemplateId(source.internalTemplateId || source.internal_template_id)
            .withTemplateParameters(source.templateParameters || source.template_parameters)
            .withExtraParameters(source.extraParameters || source.extra_parameters)
            .withIssues(source.issues)
            .withModifyBy(source.modifyBy || source.modify_by)
            .withBusinessAccountId(source.businessAccountId || source.business_account_id)
            .withDateCreated(source.dateCreated || source.date_created)
            .withDateUpdated(source.dateUpdated || source.date_updated)
            .withVariables(source.variables || [])
            .withButtons(source.buttons || [])
            .build();

        if (!state.templatesList) return;

        const updatedTemplatesList = { ...state.templatesList };
        updatedTemplatesList.content = [...state.templatesList.content];
        const templateIndex = updatedTemplatesList.content.findIndex((item) => item.uuid === template.uuid);
        if (templateIndex === -1) return;

        switch (event.name) {
            case 'TemplateUpdated':
                updatedTemplatesList.content[templateIndex] = template;
                commit('SET_TEMPLATE_LIST', updatedTemplatesList);
                // Selección explícita: aquí no existe `fetchCurrentTemplate`.
                if (state.currentTemplate?.uuid === template.uuid) dispatch('selectTemplate', template);
                break;
            case 'TemplateScoringUpdated':
                updatedTemplatesList.content[templateIndex] = {
                    ...updatedTemplatesList.content[templateIndex],
                    score: template.score,
                };
                commit('SET_TEMPLATE_LIST', updatedTemplatesList);
                break;
            default:
                console.warn(`Unhandled template event: ${event.name}`);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
